import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import _reduceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/reduce';
import _bindInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/bind';
import _Object$assign from '@babel/runtime-corejs3/core-js-stable/object/assign';
import _forEachInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/for-each';
import _Object$keys from '@babel/runtime-corejs3/core-js-stable/object/keys';
import { deflate, inflate } from '@thisisbud/object-utils';
import { number, dateTime } from '@thisisbud/intl-formats';
import { getCompareFn, formatCurrency, formatDateTime, formatUTCDateTime, formatList, formatNumber, formatPercentage, getCurrencyName, getCurrencySymbol, getPluralRule, smartFormatDate, smartFormatDateRange } from '@thisisbud/intl-utils';
import _parseFloat from '@babel/runtime-corejs3/core-js-stable/parse-float';
import _sliceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/slice';
import _trimInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/trim';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.string.replace.js';
import * as moo from 'moo';
import 'core-js/modules/web.dom-collections.iterator.js';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';

const arrayExpressionType = 'ArrayExpression';
const callExpressionType = 'CallExpression';
const identifierType$1 = 'Identifier';
const literalType = 'Literal';
const objectExpressionType = 'ObjectExpression';
const templateLiteralType = 'TemplateLiteral';

const booleanType = 'boolean';
const bracketEndType = 'bracketEnd';
const bracketStartType = 'bracketStart';
const commaType = 'comma';
const curlyEndType = 'curlyEnd';
const curlyStartType = 'curlyStart';
const expressionStartType = 'expressionStart';
const functionCallType = 'functionCall';
const identifierType = 'identifier';
const nullType = 'null';
const numberType = 'number';
const parenEndType = 'parenEnd';
const propertyNameType = 'propertyName';
const stringType = 'string';
const templateEndType = 'templateEnd';
const templateStartType = 'templateStart';
const undefinedType = 'undefined';

/**
 * Get the function identifier from a function call.
 *
 * @param value - A value from which to extract the contents
 * @returns A function identifier
 */
function trimFunctionCall(value) {
  return value.replace(/[\s(]/g, '');
}
/**
 * Get the property name from a property declaration.
 *
 * @param value - A value from which to extract the contents
 * @returns A property name
 */
function trimPropertyName(value) {
  return value.replace(/[\s:']/g, '');
}
/**
 * Get the contents from a string expression.
 *
 * @param value - A value from which to extract the contents
 * @returns The contents of a string expression
 */
function trimString(value) {
  var _context;
  return _sliceInstanceProperty(_context = _trimInstanceProperty(value).call(value)).call(_context, 1, -1);
}
/**
 * Removes leading and trailing white space characters from a string.
 *
 * @param value - A value to be trimmed
 * @returns The given value without leading or trailing white space characters
 */
function trimValue(value) {
  return _trimInstanceProperty(value).call(value);
}
/* eslint-disable sort-keys */
const states = {
  body: {
    [expressionStartType]: {
      match: '${',
      push: 'expression'
    },
    [curlyEndType]: {
      match: /\s*\}/
    },
    [stringType]: {
      lineBreaks: true,
      match: /(?:[^$]|\$(?!\{))+/
    }
  },
  expression: {
    [propertyNameType]: {
      match: /\s*(?:'[\w-]+'|[\w-]+):\s*/,
      value: trimPropertyName
    },
    [bracketStartType]: {
      match: /\s*\[\s*/,
      push: 'expression'
    },
    [bracketEndType]: {
      match: /\s*\]\s*/,
      pop: 1
    },
    [curlyStartType]: {
      match: /\s*\{\s*/,
      push: 'expression'
    },
    [curlyEndType]: {
      match: /\s*\}(?:\s*(?=[,}\])]))?/,
      pop: 1
    },
    [parenEndType]: {
      match: /\s*\)\s*/,
      pop: 1
    },
    [templateStartType]: {
      match: /\s*`/,
      push: 'template'
    },
    [stringType]: [{
      match: /\s*"(?:\\["\\rn]|[^"\\])*?"\s*/,
      value: trimString
    }, {
      match: /\s*'(?:\\['\\rn]|[^'\\])*?'\s*/,
      value: trimString
    }],
    [commaType]: {
      match: /\s*,\s*/
    },
    [booleanType]: {
      match: /\s*(?:true|false)\s*/,
      value: trimValue
    },
    [nullType]: {
      match: /\s*null\s*/
    },
    [numberType]: {
      match: /\s*-?\d+(?:\.\d+)?\s*/,
      value: trimValue
    },
    [undefinedType]: {
      match: /\s*undefined\s*/
    },
    [functionCallType]: {
      match: /\s*\w+\s*\(\s*/,
      push: 'expression',
      value: trimFunctionCall
    },
    [identifierType]: {
      match: /\s*\w+\s*/,
      value: trimValue
    }
  },
  template: {
    [expressionStartType]: {
      match: '${',
      push: 'expression'
    },
    [templateEndType]: {
      match: /`\s*/,
      pop: 1
    },
    [stringType]: {
      lineBreaks: true,
      match: /(?:[^$`]|\$(?!\{))+/
    }
  }
};
/* eslint-enable sort-keys */
const lexer = moo.states(states);
/**
 * Translate a translation string into tokens.
 *
 * @param input - The translation to be tokenized
 * @returns The tokens that make up the translation string
 */
function tokenize(input) {
  const output = [];
  const tokens = lexer.reset(input);
  let currentToken = tokens.next();
  while (typeof currentToken !== 'undefined') {
    output.push(currentToken);
    currentToken = tokens.next();
  }
  return output;
}

/**
 * Throw an error when invalid syntax is encountered.
 */
function onInvalidSyntax() {
  throw new Error('Invalid translation syntax');
}
/**
 * Parse an identifier token into its corresponding AST object.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns An identifier AST object
 */
function parseIdentifier(tokens) {
  const {
    value
  } = tokens.shift();
  return {
    name: value,
    type: identifierType$1
  };
}
/**
 * Parses supported literal value tokens into their corresponding AST objects.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns A literal value AST object
 */
function parseLiteral(tokens) {
  const {
    type,
    value
  } = tokens.shift();
  let parsedValue;
  switch (type) {
    case booleanType:
      parsedValue = value === 'true';
      break;
    case nullType:
      parsedValue = null;
      break;
    case numberType:
      parsedValue = _parseFloat(value);
      break;
    case undefinedType:
      parsedValue = undefined;
      break;
    default:
      parsedValue = value;
  }
  return {
    type: literalType,
    value: parsedValue
  };
}
/**
 * Parses an array and array element tokens into their corresponding AST objects.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns An array AST object
 */
function parseArray(tokens) {
  tokens.shift(); // Remove the opening bracket from the tokens
  const elements = [];
  const firstToken = tokens[0];
  let expressionOpen = true;
  // Handle `undefined` shorthand as first element
  if (firstToken.type === commaType) {
    elements.push({
      type: literalType,
      value: undefined
    });
  }
  while (tokens.length && expressionOpen) {
    const token = tokens[0];
    const nextToken = tokens[1];
    switch (token.type) {
      case commaType:
        // Handle `undefined` shorthand
        if (typeof nextToken !== undefinedType && (nextToken.type === commaType || nextToken.type === parenEndType)) {
          elements.push({
            type: literalType,
            value: undefined
          });
        }
        tokens.shift();
        break;
      // Handle the closing of the array
      case bracketEndType:
        expressionOpen = false;
        tokens.shift();
        break;
      // Handle array elements
      default:
        elements.push(parseExpression(tokens));
      // eslint-disable-line @typescript-eslint/no-use-before-define
    }
  }

  return {
    elements: elements,
    type: arrayExpressionType
  };
}
/**
 * Parses an object and key-value pair tokens into their corresponding AST objects.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns An object AST object
 */
function parseObject(tokens) {
  tokens.shift(); // Remove the opening curly brace from the tokens
  const props = {};
  let expressionOpen = true;
  while (tokens.length && expressionOpen) {
    const token = tokens.shift();
    switch (token.type) {
      case commaType:
        break;
      // Handle the closing of the object
      case curlyEndType:
        expressionOpen = false;
        break;
      // Handle key and value pairs
      default:
        if (token.type !== propertyNameType) {
          return onInvalidSyntax();
        }
        props[token.value] = parseExpression(tokens);
      // eslint-disable-line @typescript-eslint/no-use-before-define
    }
  }

  return {
    properties: props,
    type: objectExpressionType
  };
}
/**
 * Parses a function call and argument tokens into their corresponding AST objects.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns A function call AST object
 */
function parseFunctionCall(tokens) {
  const fnCall = tokens.shift();
  const args = [];
  const firstToken = tokens[0];
  let expressionOpen = true;
  // Handle `undefined` shorthand as first argument
  if (firstToken.type === commaType) {
    args.push({
      type: literalType,
      value: undefined
    });
  }
  while (tokens.length && expressionOpen) {
    const token = tokens[0];
    const nextToken = tokens[1];
    switch (token.type) {
      case commaType:
        // Handle `undefined` shorthand
        if (typeof nextToken !== 'undefined' && (nextToken.type === commaType || nextToken.type === 'parenEnd')) {
          args.push({
            type: literalType,
            value: undefined
          });
        }
        tokens.shift();
        break;
      // Handle the closing of the function call
      case 'parenEnd':
        expressionOpen = false;
        tokens.shift();
        break;
      // Handle arguments
      default:
        args.push(parseExpression(tokens));
      // eslint-disable-line @typescript-eslint/no-use-before-define
    }
  }

  return {
    arguments: args,
    callee: fnCall.value,
    type: callExpressionType
  };
}
/**
 * Parses a string template literal and expression tokens into their corresponding AST objects.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns A string template literal AST object
 */
function parseTemplateLiteral(tokens) {
  tokens.shift(); // Remove the opening back tick from the tokens
  const parts = [];
  let templateOpen = true;
  let expressionOpen = false;
  while (tokens.length && templateOpen) {
    const token = tokens[0];
    switch (token.type) {
      case stringType:
        parts.push(parseLiteral(tokens));
        break;
      // Handle the closing of the template literal
      case templateEndType:
        if (expressionOpen) {
          return onInvalidSyntax();
        }
        templateOpen = false;
        tokens.shift();
        break;
      case expressionStartType:
        expressionOpen = true;
        tokens.shift();
        parts.push(parseExpression(tokens)); // eslint-disable-line @typescript-eslint/no-use-before-define
        break;
      case curlyEndType:
        expressionOpen = false;
        tokens.shift();
        break;
      default:
        return onInvalidSyntax();
    }
  }
  return {
    parts: parts,
    type: templateLiteralType
  };
}
/**
 * Parses an expression into its corresponding AST object.
 *
 * @param tokens - A list of tokens remaining to be parsed
 * @returns An appropriate AST object for the expression
 */
function parseExpression(tokens) {
  const token = tokens[0];
  switch (token.type) {
    case identifierType:
      return parseIdentifier(tokens);
    case functionCallType:
      return parseFunctionCall(tokens);
    case bracketStartType:
      return parseArray(tokens);
    case curlyStartType:
      return parseObject(tokens);
    case templateStartType:
      return parseTemplateLiteral(tokens);
    default:
      return parseLiteral(tokens);
  }
}
/**
 * Parse a translation string into its AST.
 *
 * @remarks Although based on ESTree, the AST objects do not follow those conventions
 * @param input - A translation string to be parsed
 * @returns A sequence of AST objects that describe the translation string structure
 */
function parse(input) {
  const tokens = tokenize(input);
  const output = [];
  while (tokens.length) {
    const token = tokens[0];
    switch (token.type) {
      case 'string':
        output.push(parseLiteral(tokens));
        break;
      case expressionStartType:
        tokens.shift();
        output.push(parseExpression(tokens));
        break;
      case curlyEndType:
        tokens.shift();
        break;
      default:
        return onInvalidSyntax();
    }
  }
  return output;
}

/**
 * Throw an error when an unknown AST type is encountered.
 */
function onUnknownAstType() {
  throw new Error('Unknown AST type');
}
/**
 * Interpret an array AST object.
 *
 * @param astObject - The AST object to be interpreted
 * @param locals - Variables to use in the local context
 * @returns The resulting array from the interpretation
 */
function interpretArray(astObj, locals) {
  const {
    elements
  } = astObj;
  return _mapInstanceProperty(elements).call(elements, function (elementAst) {
    return interpretAst(elementAst, locals); // eslint-disable-line @typescript-eslint/no-use-before-define
  });
}
/**
 * Interpret a function call AST object.
 *
 * @param astObject - The AST object to be interpreted
 * @param locals - Variables to use in the local context
 * @returns The value returned by the function call
 */
function interpretFunctionCall(astObj, locals) {
  const {
    arguments: args,
    callee
  } = astObj;
  return locals[callee](...interpret(args, locals)); // eslint-disable-line @typescript-eslint/no-use-before-define
}
/**
 * Interpret an identifier AST object.
 *
 * @param astObject - The AST object to be interpreted
 * @param locals - Variables to use in the local context
 * @returns The value stored in the local context with the corresponding identifier name
 */
function interpretIdentifier(astObj, locals) {
  return locals[astObj.name];
}
/**
 * Interpret a literal AST object.
 *
 * @param astObj - The AST object to be interpreted
 * @returns The value of the literal.
 */
function interpretLiteral(astObj) {
  return astObj.value;
}
/**
 * Interpret an object AST object.
 *
 * @param astObject - The AST object to be interpreted
 * @param locals - Variables to use in the local context
 * @returns The resulting object from the interpretation
 */
function interpretObject(astObj, locals) {
  var _context;
  const {
    properties
  } = astObj;
  const output = {};
  _forEachInstanceProperty(_context = _Object$keys(properties)).call(_context, function (key) {
    output[key] = interpretAst(properties[key], locals); // eslint-disable-line @typescript-eslint/no-use-before-define
  });

  return output;
}
/**
 * Interpret a string template literal AST object.
 *
 * @param astObject - The AST object to be interpreted
 * @param locals - Variables to use in the local context
 * @returns The string resulting from rendering the template literal
 */
function interpretTemplateLiteral(astObject, locals) {
  const {
    parts
  } = astObject;
  return interpret(parts, locals).join(''); // eslint-disable-line @typescript-eslint/no-use-before-define
}
/**
 * Generic AST object interpreter which routes AST objects to the appropriate handlers.
 *
 * @param astObj - The AST object to be interpreted
 * @param locals - Variables to use in the local context
 * @returns The result of the interpretation for the given local context
 */
function interpretAst(astObj, locals) {
  const {
    type
  } = astObj;
  switch (type) {
    case arrayExpressionType:
      return interpretArray(astObj, locals);
    case callExpressionType:
      return interpretFunctionCall(astObj, locals);
    case identifierType$1:
      return interpretIdentifier(astObj, locals);
    case literalType:
      return interpretLiteral(astObj);
    case objectExpressionType:
      return interpretObject(astObj, locals);
    case templateLiteralType:
      return interpretTemplateLiteral(astObj, locals);
    default:
      return onUnknownAstType();
  }
}
/**
 * Interpret a translation AST with the given locals.
 * Any function calls will be executed and variable identifiers will be replaced with their values.
 *
 * @param ast - The AST objects that describe the translation string structure
 * @param locals - Variables to use in the local context of the translation
 * @returns An array containing the different parts of the interpretation
 */
function interpret(ast, locals) {
  return _mapInstanceProperty(ast).call(ast, function (astObj) {
    return interpretAst(astObj, locals);
  });
}

/**
 * Resolve a translation key in dot notation format to a translation string in the store.
 *
 * @param key - The key to resolve
 * @param translations - The translations store
 * @returns The resolved translation if found
 */
function resolveDotNotationKey(key, translations) {
  const parts = key.split('.');
  try {
    const output = _reduceInstanceProperty(parts).call(parts, function (acc, part) {
      return acc[part];
    }, translations);
    return typeof output === 'string' ? output : undefined;
  } catch (err) {
    return undefined;
  }
}
/**
 * Default handler for missing translations.
 *
 * @param key - The missing translation key
 */
function onMissingTranslation(locale, key) {
  throw new Error(`The locale '${locale}' is missing the translation with the key '${key}'`);
}
/**
 * Resolve a given key to the associated translation AST.
 *
 * @param locale - The locale in use for formatting
 * @param key - The translation key to resolve
 * @param translations - Store for the translations associated with the locale in use
 * @param parsedTranslations - Store for already parsed translations associated with the locale in use
 * @returns The translation AST that resulted from resolving the given key
 */
function resolveTranslation(locale, key, translations, parsedTranslations) {
  if (typeof parsedTranslations[key] !== 'undefined') {
    return parsedTranslations[key];
  }
  const rawTranslation = resolveDotNotationKey(key, translations);
  if (typeof rawTranslation !== 'string') {
    return onMissingTranslation(locale, key);
  }
  const translation = parse(rawTranslation);
  parsedTranslations[key] = translation;
  return translation;
}
/**
 * Extends the default formats with custom formats.
 *
 * @param formats - Different format settings for different data types
 * @returns The default formats combined with the given custom formats
 */
function extendFormats(formats = {}) {
  return {
    currency: _objectSpread({
      default: number.currency,
      screenReader: number.currencyScreenReader,
      screenReaderSigned: number.currencyScreenReaderSigned,
      screenReaderUnsigned: number.currencyScreenReaderUnsigned,
      signed: number.currencySigned
    }, formats.currency),
    dateTime: _objectSpread({
      default: dateTime.default,
      fullDate: dateTime.fullDate,
      localTime: dateTime.localTime,
      longDate: dateTime.longDate,
      longDateLocalTime: dateTime.longDateLocalTime,
      longDateTime: dateTime.longDateTime,
      mediumDate: dateTime.mediumDate,
      shortDate: dateTime.shortDate,
      time: dateTime.time
    }, formats.dateTime),
    number: _objectSpread({
      decimal: number.decimal,
      default: number.default,
      integer: number.integer
    }, formats.number),
    percentage: _objectSpread({
      default: number.percent
    }, formats.percentage)
  };
}
class I18n {
  /**
   * The locale in use for formatting
   */

  /**
   * The available formats to use with the data formatting helpers
   */

  /**
   * Store for the translations associated with the locale
   */

  /**
   * Compares two strings according to the sort order of `compareFnOptions`.
   *
   * @param x - The left string to compare
   * @param y - The right string to compare
   */

  /**
   * The local context to use when interpolating translations
   */

  /**
   * Store for the parsed translations associated with the locale
   */

  /**
   * Create an instance of the I18n class which you can use to format data in a specific language and different formats.
   *
   * @param locale - The locale to use for formatting
   * @param options - Customize the way data is formatted
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB', {
   *   translations: {
   *     foobar: 'The quick brown fox jumps over the lazy dog',
   *   },
   * })
   * ```
   */
  constructor(locale, options = {}) {
    var _context, _context2, _context3, _context4, _context5, _context6, _context7, _context8, _context9, _context10, _context11, _context12, _context13, _context14, _context15;
    _defineProperty(this, "locale", void 0);
    _defineProperty(this, "formats", void 0);
    _defineProperty(this, "translations", {});
    _defineProperty(this, "compare", void 0);
    _defineProperty(this, "context", {});
    _defineProperty(this, "parsedTranslations", {});
    const {
      compareFnOptions,
      formats
    } = options;
    this.formats = extendFormats(formats);
    const boundFormatCurrency = _bindInstanceProperty(_context = this.formatCurrency).call(_context, this);
    const boundFormatDateTime = _bindInstanceProperty(_context2 = this.formatDateTime).call(_context2, this);
    const boundFormatList = _bindInstanceProperty(_context3 = this.formatList).call(_context3, this);
    const boundFormatNumber = _bindInstanceProperty(_context4 = this.formatNumber).call(_context4, this);
    const boundFormatPercentage = _bindInstanceProperty(_context5 = this.formatPercentage).call(_context5, this);
    const boundT = _bindInstanceProperty(_context6 = this.t).call(_context6, this);
    const boundTToParts = _bindInstanceProperty(_context7 = this.tToParts).call(_context7, this);
    const boundFormatUTCDateTime = _bindInstanceProperty(_context8 = this.formatUTCDateTime).call(_context8, this);
    const boundGetCurrencyName = _bindInstanceProperty(_context9 = this.getCurrencyName).call(_context9, this);
    const boundGetCurrencySymbol = _bindInstanceProperty(_context10 = this.getCurrencySymbol).call(_context10, this);
    const boundGetOrdinalRule = _bindInstanceProperty(_context11 = this.getOrdinalRule).call(_context11, this);
    const boundGetPluralRule = _bindInstanceProperty(_context12 = this.getPluralRule).call(_context12, this);
    const boundSelectPhrase = _bindInstanceProperty(_context13 = this.selectPhrase).call(_context13, this);
    const boundSmartFormatDate = _bindInstanceProperty(_context14 = this.smartFormatDate).call(_context14, this);
    const boundSmartFormatDateRange = _bindInstanceProperty(_context15 = this.smartFormatDateRange).call(_context15, this);
    this.compare = getCompareFn(locale, compareFnOptions);
    this.formatCurrency = boundFormatCurrency;
    this.formatDateTime = boundFormatDateTime;
    this.formatList = boundFormatList;
    this.formatNumber = boundFormatNumber;
    this.formatPercentage = boundFormatPercentage;
    this.t = boundT;
    this.tToParts = boundTToParts;
    this.formatUTCDateTime = boundFormatUTCDateTime;
    this.getCurrencyName = boundGetCurrencyName;
    this.getCurrencySymbol = boundGetCurrencySymbol;
    this.getOrdinalRule = boundGetOrdinalRule;
    this.getPluralRule = boundGetPluralRule;
    this.selectPhrase = boundSelectPhrase;
    this.smartFormatDate = boundSmartFormatDate;
    this.smartFormatDateRange = boundSmartFormatDateRange;
    this.locale = locale;
    this.extendContext(_objectSpread({
      formatCurrency: boundFormatCurrency,
      formatDateTime: boundFormatDateTime,
      formatList: boundFormatList,
      formatNumber: boundFormatNumber,
      formatPercentage: boundFormatPercentage,
      formatUTCDateTime: boundFormatUTCDateTime,
      getCurrencyName: boundGetCurrencyName,
      getCurrencySymbol: boundGetCurrencySymbol,
      getOrdinalRule: boundGetOrdinalRule,
      getPluralRule: boundGetPluralRule,
      selectPhrase: boundSelectPhrase,
      smartFormatDate: boundSmartFormatDate,
      smartFormatDateRange: boundSmartFormatDateRange,
      t: boundT,
      tToParts: boundTToParts
    }, options.defaultContext));
    this.extendTranslations(_objectSpread({}, options.translations));
  }
  /**
   * Extends the default interpolation context associated with the I18n instance. New properties can be added and existing properties can be replaced.
   *
   * @param ctx - The interpolation context to extend the existing context with
   * @returns The I18n instance will be returned for chaining
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB').extendContext({ foo: 'bar' })
   * ```
   */
  extendContext(ctx) {
    const existingCtx = this.context;
    this.context = _objectSpread(_objectSpread({}, existingCtx), ctx);
    return this;
  }
  /**
   * Extends the translations associated with the I18n instance. New translations can be added and existing translations can be replaced.
   *
   * @remarks Objects will be deflated into a shallow object of dot notation keys
   * @param translations - The translations to extend the existing translations with
   * @returns The I18n instance will be returned for chaining
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB').extendTranslations({ foo: 'bar' })
   * ```
   */
  extendTranslations(newTranslations) {
    var _context16;
    const translations = this.translations;
    const parsedTranslations = this.parsedTranslations;
    // @ts-expect-error The types for `deflate()` don't accept arrays but the function does work with arrays
    const deflatedTransalations = deflate(newTranslations);
    _Object$assign(translations, inflate(_objectSpread(_objectSpread({}, deflate(translations)), deflatedTransalations)));
    // Delete cached parsed translations being replaced
    _forEachInstanceProperty(_context16 = _Object$keys(deflatedTransalations)).call(_context16, function (key) {
      if (deflatedTransalations[key] === null) {
        delete translations[key];
      }
      delete parsedTranslations[key];
    });
    return this;
  }
  /**
   * Verify if a given translation exists in the I18n instance.
   *
   * @param key - The translation key to verify the existence of
   * @returns `true` or `false` depending on wether the translation associated with the given key exists or not
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB').extendTranslations({ foo: 'bar' })
   * i18n.hasTranslation('foo') // true
   * i18n.hasTranslation('bar') // false
   * ```
   */
  hasTranslation(key) {
    return typeof resolveDotNotationKey(key, this.translations) === 'string';
  }
  /**
   * Format a currency value according to the conventions of the instance's locale.
   *
   * @param value - A currency amount to format
   * @param currency - The ISO 4217 currency code in which the value is to be represented
   * @param format - The key of the format to use when formatting the currency
   * @returns A currency value formatted according to the conventions of the instance's locale
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.formatCurrency(12.5, 'GBP')
   * // '£12.50'
   * ```
   */
  formatCurrency(value, currency, format = 'default') {
    const formatOptions = this.formats.currency[format];
    return formatCurrency(this.locale, value, currency, formatOptions);
  }
  /**
   * Format a date and time according to the conventions of the instance's locale.
   *
   * @param value - A date object to format
   * @param format - The key of the format to use when formatting the date
   * @returns A date formatted according to the conventions of the instance's locale
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.formatDateTime(new Date(), 'shortDate')
   * // '17/12/2020'
   * ```
   */
  formatDateTime(value, format = 'default') {
    const formatOptions = this.formats.dateTime[format];
    return formatDateTime(this.locale, value, formatOptions);
  }
  /**
   * Format a date and time according to the conventions of the instance's locale and universal time.
   *
   * @param value - A date object to format
   * @param format - The key of the format to use when formatting the date
   * @returns A date formatted according to the conventions of the instance's locale and universal time
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.formatUTCDateTime(new Date(), 'shortDate')
   * // '17/12/2020'
   * ```
   */
  formatUTCDateTime(value, format = 'default') {
    const formatOptions = this.formats.dateTime[format];
    return formatUTCDateTime(this.locale, value, formatOptions);
  }
  /**
   * Format a list of strings according to language conventions.
   *
   * @param value - The list of strings to format
   * @param type - The type of list to format
   * @returns A formatted list according to language conventions
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.formatList(['foo', 'bar'], 'conjunction')
   * // 'foo and bar'
   * ```
   */
  formatList(value, type = 'conjunction') {
    return formatList(this.locale, value, type);
  }
  /**
   * Format a number according to the conventions of the instance's locale.
   *
   * @param value - A numeric value to format
   * @param format - The key of the format to use when formatting the number
   * @returns A number formatted according to the conventions of the instance's locale
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.formatNumber(1200, 'integer')
   * // '1,200'
   * ```
   */
  formatNumber(value, format = 'default') {
    const formatOptions = this.formats.number[format];
    return formatNumber(this.locale, value, formatOptions);
  }
  /**
   * Format a percentage according to the conventions of the instance's locale.
   *
   * @param value - A numeric value to format with `1` being equivalent to 100%
   * @param format - The key of the format to use when formatting the percentage
   * @returns A percentage formatted according to the conventions of the instance's locale
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.formatPercentage(0.125)
   * // '12.5%'
   * ```
   */
  formatPercentage(value, format = 'default') {
    const formatOptions = this.formats.percentage[format];
    return formatPercentage(this.locale, value, formatOptions);
  }
  /**
   * Get the human-readable name of a currency.
   *
   * @param currency - The ISO 4217 currency code
   * @param plural - Set to `true` to get the name in the plural form
   * @returns The name of the currency
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.getCurrencyName('GBP)
   * // 'British pound'
   * ```
   */
  getCurrencyName(currency, plural) {
    return getCurrencyName(this.locale, currency, plural);
  }
  /**
   * Get the symbol for a currency code.
   *
   * @param currency - The ISO 4217 currency code
   * @returns The symbol corresponding to the given currency code
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   * const result = i18n.getCurrencySymbol('GBP)
   * // '£'
   * ```
   */
  getCurrencySymbol(currency) {
    return getCurrencySymbol(currency);
  }
  /**
   * Get which ordinal rule to use for locale-aware formatting.
   *
   * @param value - The number to get an ordinal rule for
   * @returns The ordinal category of the number
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   *
   * const result = i18n.getOrdinalRule(1)
   * // 'one'
   * ```
   */
  getOrdinalRule(value) {
    return getPluralRule(this.locale, value, true);
  }
  /**
   * Get which plural rule to use for locale-aware formatting.
   *
   * @param value - The number to get a plural rule for
   * @returns The pluralization category of the number
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   *
   * const result = i18n.getPluralRule(1)
   * // 'one'
   * ```
   */
  getPluralRule(value) {
    return getPluralRule(this.locale, value, false);
  }
  selectPhrase(value, type, phrases) {
    const availablePhrases = phrases;
    let key;
    switch (type) {
      case 'plural':
        key = this.getPluralRule(value);
        break;
      case 'ordinal':
        key = this.getOrdinalRule(value);
        break;
      default:
        key = value;
        break;
    }
    if (typeof availablePhrases[key] === 'string') {
      return availablePhrases[key];
    } else if (typeof availablePhrases.default === 'string') {
      return availablePhrases.default;
    }
    throw new Error(`Missing selectable phrase key: ${key}`);
  }
  /**
   * Format a date according to language conventions in the context of the reference date.
   *
   * @param value - A date object to format
   * @param format - The key of the format to use when formatting the date
   * @param reference - A date to use as reference, defaults to the current date
   * @returns A date formatted according to language conventions
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   *
   * // Given the current year is 2022
   * const resultA = i18n.smartFormatDate(new Date(2020, 11, 17))
   * // '17 Dec 2020'
   *
   * const resultB = i18n.smartFormatDate(new Date())
   * // '17 Dec'
   * ```
   */
  smartFormatDate(value, format = 'default', reference) {
    const formatOptions = this.formats.dateTime[format];
    return smartFormatDate(this.locale, value, formatOptions, reference);
  }
  /**
   * Format a date range according to language conventions and the context of the given dates.
   * Start date elements will be omitted where possible.
   *
   * @param from - A start date object to format
   * @param to - An end date object to format
   * @param format - The key of the format to use when formatting the date
   * @returns An array containing the formatted start and end dates as strings or a single string array if the dates are the same
   * @public
   *
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB')
   *
   * const output = i18n.smartFormatDateRange(new Date(2020, 11, 1), new Date(2020, 11, 31))
   * // ['1', '31 Dec 2020']
   * ```
   */
  smartFormatDateRange(from, to, format = 'default') {
    const formatOptions = this.formats.dateTime[format];
    return smartFormatDateRange(this.locale, from, to, formatOptions);
  }
  /**
   * Get the parts of a translation for the given key.
   * Translations can have placeholders in the format of `${expression}`, similar to ES template literals.
   * Placeholders will be replaced with corresponding values from the `ctx` object.
   *
   * @remarks When interpolating React components into the translation this is the preferred method as it will preserve the output of their render method
   * @param key - The key of the translation to be obtained
   * @param ctx - The local context to use when interpolating the translation
   * @returns The parts of the interpolated translation associated with the given key
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB', {
   *   translations: {
   *     foobar: 'The quick brown ${getAnimal()} jumps over the lazy dog',
   *   },
   * })
   * const translation = i18n.tToParts('foobar', { getAnimal: function () { return 'fox' } })
   * // ['The quick brown ', 'fox', ' jumps over the lazy dog']
   * ```
   */
  tToParts(key, ctx) {
    const locale = this.locale;
    const ast = resolveTranslation(locale, key, this.translations, this.parsedTranslations);
    return interpret(ast, _objectSpread(_objectSpread({}, this.context), ctx));
  }
  /**
   * Get a translation for the given key.
   * Translations can have placeholders in the format of `${expression}`, similar to ES template literals.
   * Placeholders will be replaced with corresponding values from the `ctx` object.
   *
   * @remarks All placeholders will be cast into strings regardless of data type
   * @param key - The key of the translation to be obtained
   * @param ctx - The local context to use when interpolating the translation
   * @returns The interpolated translation associated with the given key
   * @public
   *
   * @example
   * ```javascript
   * import I18n from '@thisisbud/i18n'
   *
   * const i18n = new I18n('en-GB', {
   *   translations: {
   *     foobar: 'The quick brown ${getAnimal()} jumps over the lazy dog',
   *   },
   * })
   * const translation = i18n.t('foobar', { getAnimal: function () { return 'fox' } })
   * // 'The quick brown fox jumps over the lazy dog'
   * ```
   */
  t(key, ctx) {
    return this.tToParts(key, ctx).join('');
  }
}

export { I18n as default };
